import React, { useEffect, useRef, useState } from 'react'
import { Button, Skeleton } from 'antd'
import { customerService } from '../../services';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import TermAndConditionCustomerPDF from './TermAndConditionCustomerPDF';
import jsPDF from 'jspdf';
import html2pdf from 'html2pdf.js';
import { COMPANY_LOGO } from '../../constants';
import { MdOutlineFileDownload } from "react-icons/md";

function TermAndConditionCustomer() {
    const { id } = useParams()
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const encodedData = params.get("data");
    let decodedData = {};

    const [user, setUser] = useState([])
    const [loading, setLoading] = useState(true); // loading state
    const [downloading, setDownloading] = useState(false);
    const [imagesReady, setImagesReady] = useState(false); // ✅ NEW: Track image loading
    const [pdfBlob, setPdfBlob] = useState(null);

    const componentRef = useRef();

    if (encodedData) {
        decodedData = JSON.parse(decodeURIComponent(encodedData));
    }

    const loadUser = async () => {
        setLoading(true);
        const response = await customerService.view(id)
        if (response.data) {
            setUser(response.data.customer)
        }
        setLoading(false);
    }

    const handlePrint = () => {
        window.print();
    }

    const generatePDFBlob = async () => {
        try {
            console.log('Generating PDF...');

            const opt = {
                margin: 0,
                image: { type: "jpeg", quality: 0.95 },
                html2canvas: {
                    scale: 2,
                    useCORS: true,
                    allowTaint: true,
                    imageTimeout: 15000,
                    logging: false,
                },
                jsPDF: {
                    unit: "px",
                    format: [1400, 1123],
                    orientation: "portrait",
                    compress: true
                },
            };

            const blob = await html2pdf()
                .set(opt)
                .from(componentRef.current)
                .output('blob');

            setPdfBlob(blob);
            console.log('PDF generated successfully');
        } catch (error) {
            console.error('PDF generation failed:', error);
        }
    };

    const handleDownload = async () => {
        setDownloading(true);

        try {
            if (pdfBlob) {
                // Instant download from pre-generated blob
                const url = URL.createObjectURL(pdfBlob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `Negotiated_Rate_Agreement_${user?.name}.pdf`;
                link.click();
                URL.revokeObjectURL(url);
            } else {
                // Fallback: generate on-demand
                await generateAndDownload();
            }
        } catch (error) {
            console.error('Download failed:', error);
        } finally {
            setDownloading(false);
        }
    };

    const generateAndDownload = async () => {
        const opt = {
            margin: 0,
            filename: `Negotiated_Rate_Agreement_${user?.name}.pdf`,
            image: { type: "jpeg", quality: 0.95 },
            html2canvas: {
                scale: 2,
                useCORS: true,
                logging: false,
                allowTaint: false
            },
            jsPDF: {
                unit: "px",
                format: [1400, 1123],
                orientation: "portrait",
                compress: true
            },
        };

        await html2pdf().set(opt).from(componentRef.current).save();
    };

    const fixImageUrl = (url) => {
        if (!url) return null;
        return url.replace('/upload/', '/uploads/');
    };

    const handleDocumentDownload = async (url, filename) => {
        try {
            const response = await fetch(url, {
                method: "GET",
                mode: "cors", // important
            });

            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = blobUrl;
            link.setAttribute("download", filename || "file.jpg");

            document.body.appendChild(link);
            link.click();

            // cleanup
            setTimeout(() => {
                document.body.removeChild(link);
                window.URL.revokeObjectURL(blobUrl);
            }, 100);

        } catch (error) {
            console.error("Download failed:", error);
        }
    };

    useEffect(() => {
        loadUser()
    }, []);

    useEffect(() => {
        if (user?.name && componentRef.current) {
            setTimeout(() => {
                generatePDFBlob();
            }, 800);
        }
    }, [user]);

    return (
        <div style={{ backgroundColor: "#FFFFFF" }}>
            {/* <div className='flex '> */}

            <div className='border-b-2'>
                <div className='flex justify-between items-center p-4'>
                    <div className='flex items-center gap-5' >
                        <img src={COMPANY_LOGO} style={{ height: 60, width: 200 }} />
                        {/* <div> */}
                        <p className='p-0 m-0 text-[#9CA3AF] text-lg mt-9'>Negotiated Rate Agreement</p>
                        {/* <p className='p-0 m-0 text-xl'>CTRADE USA - Customer Copy</p> */}
                        {/* </div> */}
                    </div>
                    <div className='flex gap-3'>
                        <Button type='default' className='rounded-full p-5'
                            onClick={handleDownload}
                            loading={downloading}
                        // disabled={!imagesReady}
                        >Download PDF</Button>
                        {/* <Button type='default' className='rounded-full p-5' onClick={handlePrint} >Print</Button> */}
                    </div>
                </div>
            </div>

            <div className='flex justify-between p-8 gap-10'>

                <div className='w-[65%]'>

                    <div className=''>
                        <h1 className='text-lg'>Agreement details</h1>
                        <p className='text-[#6B7280]'>Please read the terms and conditions for your negotiated rate agreement. You can scroll inside the box below.</p>
                    </div>

                    <div className='flex gap-3 w-full'>

                        <div className='border border-[#E5E7EB] rounded-xl p-5 w-full'>
                            <p className='text-[#9CA3AF] uppercase'>Customer</p>
                            {loading
                                ? <Skeleton.Input style={{ width: '100%' }} active size="default" />
                                : <p className='p-0 m-0 font-semibold capitalize'>{user?.name ?? '-'}</p>
                            }
                        </div>
                        <div className='border border-[#E5E7EB] rounded-xl p-5 w-full'>
                            <p className='text-[#9CA3AF] uppercase'>Agreement date</p>
                            {loading
                                ? <Skeleton.Input style={{ width: '100%' }} active size="default" />
                                : <p className='p-0 m-0 font-semibold'>{dayjs(user?.created_at).format("DD-MM-YYYY")}</p>
                            }
                        </div>
                        <div className='border border-[#E5E7EB] rounded-xl p-5 w-full'>
                            <p className='text-[#9CA3AF] uppercase'>Reference</p>
                            {loading
                                ? <Skeleton.Input style={{ width: '100%' }} active size="default" />
                                : <p className='p-0 m-0 font-semibold'>{user?.file_ref ?? '-'}</p>
                            }
                        </div>

                    </div>

                    <div className='mt-6 border bg-[#F9FAFB] border-[#E5E7EB] rounded-lg p-5 max-h-[480px] overflow-auto text-[#374151] text-[14px] leading-6'>
                        <ul className='list-decimal pl-5 space-y-3'>

                            <li>
                                This contract is made by and between <span className='font-semibold'> CTRADE USA INC (As SHIPPING/FORWARDING/EXPORTER AGENT ONLY)</span> and <span className='font-semibold capitalize'>{user?.name} </span> on <span className='font-semibold'>{dayjs(user?.created_at).format("DD-MM-YYYY")}</span> (as a shipper) for shipping requirements on agreed price according to email applied from the date of services offer or cargo delivered or handled.
                            </li>

                            <li>
                                Unless otherwise noted, all ocean freight quotations subject to third party are valid for 30 days from the date of original quotation, subject to equipment availability, subject to tariff additions, valid at time initial shipment is received.
                                Inland Freight quotations are subject to third party increases valid at time initial shipment is received, subject to fuel surcharges valid at time initial shipment is received, subject to weight limitations and weight distribution requirements according to local and national regulations of transit countries, subject to inland carrier availability at booking time.
                                Loading, lashing, securing, blocking and bracing of cargo is for shipper's account. Carrier may stow cargo as needed. On deck shipments at shipper’s risk. Dangerous cargo requires booking approval. Vessel dates may change. Equipment subject to availability. Service under this NRA is subject to Carrier's governing rules tariff per FMC Regulations 46 CFR 532.7.
                            </li>

                            <li>
                                Insurance: We advise all customers to put insurance on their shipments as there is no insurance on container.
                            </li>

                            <li>
                                Customs hold and inspection: CTRADE USA INC will not be responsible for any delay or costs incurred due to customs investigation or hold. See bill of lading Terms 1-34 in Rule 8 of carriers governing rules tariff.
                            </li>

                            <li>
                                RORO: Vehicle shipments should be empty and operational.
                            </li>

                            <li>
                                Payment: Customers must pay on time as agreed, within 15 days from invoice date. Allow 3–7 business days for payment processing and release. CTRADE USA INC is not responsible for demurrage or detention at destination due to late payment.
                            </li>

                            <li>
                                ETA and ETD: All sailing and arrival dates are estimated and CTRADE USA INC will not be responsible for any delay. See bill of lading Terms 1-34 in Rule 8 of carriers governing rules tariff.
                            </li>

                            <li>
                                Packing List: Customer must provide a proper packing list. CTRADE USA INC is not responsible for fines, fees, penalties or charges due to incorrect or missing packing list.
                            </li>

                            <li>
                                Declaring Values: Shipper must provide accurate values for documentary and customs purposes.
                            </li>

                            <li>
                                CTRADE USA INC will not be responsible for any delay in release or storage caused by shipment handling through any third party.
                            </li>

                            <li>
                                Customer will be responsible for charges incurred due to incorrect values or holds caused.
                            </li>

                            <li>
                                See CTRADE USA INC ORDER FORM for applicable shipment details.
                            </li>

                            <li>
                                Any vessel delay, date change or customs hold causing rollover or per diem is not the responsibility of CTRADE USA INC. Additional charges may apply.
                            </li>

                            <li>
                                Storage Policy: 15 days free at all facilities, thereafter 10 USD per day per car. In and out charges are 100 USD. Subject to storage rules.
                            </li>

                            <li>
                                Documentation for Title Validation: Customer must provide all required documents such as BOS, notarized POA, passport copy etc. for US Customs. If customs holds documents and rollover occurs, CTRADE USA INC is not responsible.
                            </li>

                            <li>
                                Port Conditions: CTRADE USA INC is not responsible for any unforeseen issues at Houston Port affecting shipping operations.
                            </li>

                        </ul>
                    </div>

                </div>

                <div className=' border border-[#E5E7EB] p-4 rounded-lg w-[35%] '>
                    <div className='border-b-2 pb-6 flex flex-col gap-3'>
                        <h2 className='p-0 m-0 text-base'>Summary</h2>
                        <p className='p-0 m-0'> <span className='font-semibold'>Agreement number:</span> {" "}<span className='text-[#4B5563]'>{user?.agreement_number}</span></p>
                        <p className='p-0 m-0'> <span className='font-semibold'>Created by:</span> {" "}<span className='text-[#4B5563]'>CTRADE USA INC</span></p>
                        <p className='p-0 m-0'><span className='font-semibold '>Applies to:</span> {" "}<span className='text-[#4B5563]'>All shipments booked under this account</span></p>
                    </div>

                    <div className='py-8'>
                        <p className='font-semibold'>Upload documents:</p>
                        {loading
                            ? <Skeleton.Input style={{ width: '100%' }} active size="default" />
                            : <div className='flex items-center gap-5'>
                                <img src={user?.passport} alt='image' style={{ width: 120, height: 80, textAlign: "right" }} />

                                <MdOutlineFileDownload className='!text-2xl cursor-pointer'
                                    onClick={() =>
                                        handleDocumentDownload(
                                            fixImageUrl(user?.passport),
                                            `${user?.name || "Customer"}_Passport.jpg`
                                        )
                                    }
                                />
                            </div>
                        }
                        <p className='font-semibold pt-6'>Customer signature:</p>
                        {loading
                            ? <Skeleton.Input style={{ width: '100%' }} active size="default" />
                            : <img src={user?.signature} style={{ width: 120, height: 80, textAlign: "center" }} />
                        }
                    </div>
                </div>
            </div>
            <div
                style={{
                    position: "absolute",
                    top: 0,
                    left: "-9999px",
                }}
            >
                <TermAndConditionCustomerPDF ref={componentRef}
                    user={{
                        ...user,
                        signature: fixImageUrl(user?.signature),
                        passport: fixImageUrl(user?.passport),
                    }} />
            </div>

        </div>
    )
}

export default TermAndConditionCustomer
